import { useState } from "react";
import { useI18n } from "@/contexts/I18nContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Spanish VAT options. "custom" lets the user enter any rate.
const RATE_OPTIONS = [
  { value: "0", label: "0% (Exento)" },
  { value: "4", label: "4%" },
  { value: "10", label: "10%" },
  { value: "21", label: "21%" },
  { value: "custom", label: "Otro" },
];

const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseFloat(v);
  return isFinite(n) ? n : null;
};

const fmt = (n) => (n == null ? "" : String(Math.round(n * 100) / 100));

/**
 * Controlled IVA fields. The parent owns `values = { subtotal, tax, amount, tax_rate }`
 * as strings and provides `onChange(patch)` which receives partial string updates.
 * Mode "base" → user edits subtotal, tax & total are auto-derived.
 * Mode "total" → user edits amount, subtotal & tax are auto-derived.
 */
export default function IvaFields({ values, onChange, testidPrefix = "iva" }) {
  const { t } = useI18n();
  const [mode, setMode] = useState("base"); // "base" | "total"

  // Determine which dropdown value to show. If rate matches a preset, show that; else "custom".
  const rateStr = values.tax_rate === "" || values.tax_rate == null ? "" : String(values.tax_rate);
  const presetMatch = RATE_OPTIONS.find((o) => o.value !== "custom" && o.value === rateStr);
  const selectValue = rateStr === "" ? "21" : presetMatch ? presetMatch.value : "custom";
  const [customMode, setCustomMode] = useState(selectValue === "custom");

  const applyRate = (rate, base, total) => {
    // Recompute the two derived fields from the "primary" field according to mode.
    const r = rate == null ? null : parseFloat(rate);
    if (mode === "base") {
      const b = base != null ? base : toNum(values.subtotal);
      if (b == null || r == null) return {};
      const tax = Math.round(b * r) / 100;
      const amount = Math.round((b + tax) * 100) / 100;
      return { subtotal: fmt(b), tax: fmt(tax), amount: fmt(amount), tax_rate: String(r) };
    }
    // mode = total
    const tot = total != null ? total : toNum(values.amount);
    if (tot == null || r == null) return {};
    const sub = Math.round((tot / (1 + r / 100)) * 100) / 100;
    const tax = Math.round((tot - sub) * 100) / 100;
    return { subtotal: fmt(sub), tax: fmt(tax), amount: fmt(tot), tax_rate: String(r) };
  };

  const handleRateChange = (val) => {
    if (val === "custom") {
      setCustomMode(true);
      onChange({ tax_rate: rateStr || "21" });
      return;
    }
    setCustomMode(false);
    onChange(applyRate(parseFloat(val)));
  };

  const handleCustomRateInput = (e) => {
    const v = e.target.value;
    if (v === "") {
      onChange({ tax_rate: "" });
      return;
    }
    onChange(applyRate(parseFloat(v)));
  };

  const handleBaseChange = (e) => {
    const v = e.target.value;
    if (v === "") {
      onChange({ subtotal: "" });
      return;
    }
    const r = toNum(values.tax_rate);
    if (r == null) {
      onChange({ subtotal: v });
      return;
    }
    onChange(applyRate(r, parseFloat(v), null));
  };

  const handleTotalChange = (e) => {
    const v = e.target.value;
    if (v === "") {
      onChange({ amount: "" });
      return;
    }
    const r = toNum(values.tax_rate);
    if (r == null) {
      onChange({ amount: v });
      return;
    }
    onChange(applyRate(r, null, parseFloat(v)));
  };

  return (
    <>
      {/* Input mode toggle */}
      <div className="col-span-2 flex items-center gap-2">
        <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)] shrink-0">
          {t("inputMode")}
        </Label>
        <div className="inline-flex rounded-md border border-[var(--border-color)] overflow-hidden text-xs">
          <button
            type="button"
            onClick={() => setMode("base")}
            data-testid={`${testidPrefix}-mode-base`}
            className={`px-3 py-1.5 transition-colors ${
              mode === "base"
                ? "bg-[var(--primary)] text-white"
                : "bg-[var(--surface)] hover:bg-[var(--surface-hover)]"
            }`}
          >
            {t("inputModeBase")}
          </button>
          <button
            type="button"
            onClick={() => setMode("total")}
            data-testid={`${testidPrefix}-mode-total`}
            className={`px-3 py-1.5 transition-colors ${
              mode === "total"
                ? "bg-[var(--primary)] text-white"
                : "bg-[var(--surface)] hover:bg-[var(--surface-hover)]"
            }`}
          >
            {t("inputModeTotal")}
          </button>
        </div>
      </div>

      {/* Tax-rate selector */}
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
          {t("taxRate")}
        </Label>
        <Select value={selectValue} onValueChange={handleRateChange}>
          <SelectTrigger data-testid={`${testidPrefix}-rate`} className="h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RATE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {customMode && (
          <Input
            type="number"
            step="0.01"
            min="0"
            value={rateStr}
            onChange={handleCustomRateInput}
            placeholder="%"
            data-testid={`${testidPrefix}-rate-custom`}
            className="h-9 font-mono-num mt-1"
          />
        )}
      </div>

      {/* Base */}
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
          {t("subtotal")} {mode === "base" && <span className="text-[var(--primary)]">●</span>}
        </Label>
        <Input
          type="number"
          step="0.01"
          value={values.subtotal}
          onChange={handleBaseChange}
          data-testid={`${testidPrefix}-base`}
          placeholder="0.00"
          className="font-mono-num h-9"
        />
      </div>

      {/* IVA amount (read-only computed) */}
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
          {t("tax")}
        </Label>
        <Input
          type="number"
          step="0.01"
          value={values.tax}
          onChange={(e) => onChange({ tax: e.target.value })}
          data-testid={`${testidPrefix}-tax`}
          placeholder="0.00"
          className="font-mono-num h-9 bg-[var(--surface-hover)]/40"
          readOnly={values.tax_rate !== "" && values.tax_rate != null}
        />
      </div>

      {/* Total */}
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-[var(--text-secondary)]">
          {t("amount")} {mode === "total" && <span className="text-[var(--primary)]">●</span>}
        </Label>
        <Input
          type="number"
          step="0.01"
          value={values.amount}
          onChange={handleTotalChange}
          data-testid={`${testidPrefix}-total`}
          placeholder="0.00"
          className="font-mono-num h-9"
          required
        />
      </div>
    </>
  );
}
